import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import { memo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
  supportThreadCounterpartAvatarUrl,
  supportThreadCounterpartName,
} from './participantLabel';

import { MessageCard } from '@/components/inbox/MessageCard';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import type { InboxSupportEntry } from '@/services/conversations/inbox';
import { userIdAtom } from '@/services/globalState';

type SupportThreadCardProps = {
  entry: InboxSupportEntry;
  currentTab: string;
};

const OPEN_DEBOUNCE_MS = 600;

/**
 * The row's preview of the latest message. A message sent with only files has
 * no text, and the only way a stored message can, so an empty body names that.
 */
export function lastMessagePreview(body: string | null): string {
  if (body === null) {
    return ' ';
  }

  return body.trim() ? body : 'Sent an attachment';
}

/**
 * An inbox row for a support thread. It shares the inquiry row's card so the
 * two line up, with the support badge and the other party in the subject
 * slot, as a support inquiry has always shown.
 *
 * Opening it navigates the way an inquiry row does: a push on compact
 * layouts, the selected id on wide ones. The chat route then opens the
 * support-thread view because the id is not a Twilio conversation id.
 */
export const SupportThreadCard = memo(function SupportThreadCard({
  entry,
  currentTab,
}: SupportThreadCardProps) {
  const viewerId = useAtomValue(userIdAtom);

  const isCompactLayout = useIsCompactLayout();

  const counterpartName = supportThreadCounterpartName(entry.subject, viewerId);

  const handleOpen = useDebouncedCallback(
    () => {
      const params = { id: entry.conversationId, tab: currentTab };

      if (isCompactLayout) {
        router.push({ pathname: '/inbox/chat/[id]', params });

        return;
      }

      router.setParams(params);
    },
    OPEN_DEBOUNCE_MS,
    { leading: true, trailing: false }
  );

  return (
    <MessageCard
      id={entry.conversationId}
      subject={counterpartName}
      name={counterpartName}
      hideName
      showSupportBadgeInline
      avatarUrl={supportThreadCounterpartAvatarUrl(entry.subject, viewerId)}
      message={lastMessagePreview(entry.lastMessageBody)}
      lastRespondedAt={new Date(entry.lastMessageAt ?? '')}
      unread={entry.unread}
      onPress={handleOpen}
      inquiryStatus={entry.state === 'closed' ? 'closed' : 'active'}
      currentTab={currentTab}
    />
  );
});
